import { Bot, Database, FileText, HardDrive, Plus } from "lucide-react";
import { useMemo, useState } from "react";
import { useTranslation } from "react-i18next";

import type { KnowledgeBaseWithCount } from "@/api/types";
import { useAuth } from "@/auth/useAuth";
import { EmptyState } from "@/components/feedback/EmptyState";
import { ErrorState } from "@/components/feedback/ErrorState";
import { LoadingState } from "@/components/feedback/LoadingState";
import { PageHeader } from "@/components/layout/PageHeader";
import { Badge } from "@/components/ui/badge";
import { Button } from "@/components/ui/button";
import { DataTable, type DataTableColumn } from "@/components/ui/data-table";
import { SearchInput } from "@/components/ui/search-input";
import { StatCard } from "@/components/ui/stat-card";
import { ViewToggle, useViewMode } from "@/components/ui/view-toggle";
import { KbCard } from "@/features/knowledge-bases/components/KbCard";
import { KbFormDialog } from "@/features/knowledge-bases/components/KbFormDialog";
import { useKbs } from "@/features/knowledge-bases/hooks/useKbs";
import { useKbSummary } from "@/features/observability/hooks/useObservability";
import { useTeams } from "@/features/teams/hooks/useTeams";
import { formatDate } from "@/lib/date";

export function KbListPage() {
  const { t } = useTranslation("common");
  const [showInactive, setShowInactive] = useState(false);
  const [formOpen, setFormOpen] = useState(false);
  const [search, setSearch] = useState("");
  const [teamFilter, setTeamFilter] = useState("");
  const [view, setView] = useViewMode("kb:view", "cards");
  const { user, memberships } = useAuth();
  const { data, isLoading, isError, error, refetch } = useKbs({
    include_inactive: showInactive,
  });
  const { data: teams } = useTeams({ include_inactive: false });
  const summary = useKbSummary();

  // Pode criar KB se for admin OU manager/creator de qualquer time.
  const canCreate =
    user?.is_admin ||
    memberships.some((m) => m.role === "manager" || m.role === "creator");

  const teamMap = useMemo(
    () => new Map(teams?.map((tm) => [tm.id, tm]) ?? []),
    [teams],
  );

  const filtered = useMemo(() => {
    const q = search.trim().toLowerCase();
    return (data ?? []).filter((kb) => {
      if (teamFilter && kb.owner_team_id !== teamFilter) return false;
      if (q && !kb.name.toLowerCase().includes(q)) return false;
      return true;
    });
  }, [data, search, teamFilter]);

  const columns = useMemo<DataTableColumn<KnowledgeBaseWithCount>[]>(
    () => [
      {
        key: "name",
        header: t("pages.knowledgeBases.columns.name"),
        render: (kb) => (
          <div className="flex items-center gap-2">
            <span className="font-medium text-ink-950">{kb.name}</span>
            {!kb.is_active ? (
              <Badge variant="destructive">
                {t("pages.knowledgeBases.inactive")}
              </Badge>
            ) : null}
          </div>
        ),
      },
      {
        key: "team",
        header: t("pages.knowledgeBases.columns.team"),
        render: (kb) =>
          teamMap.get(kb.owner_team_id)?.name ?? "—",
      },
      {
        key: "documents",
        header: t("pages.knowledgeBases.columns.documents"),
        render: (kb) => kb.document_count,
      },
      {
        key: "agents",
        header: t("pages.knowledgeBases.columns.agents"),
        render: (kb) => kb.agent_count,
      },
      {
        key: "created",
        header: t("pages.knowledgeBases.columns.created"),
        render: (kb) => formatDate(kb.created_at),
      },
      {
        key: "updated",
        header: t("pages.knowledgeBases.columns.updated"),
        render: (kb) => formatDate(kb.updated_at),
      },
      {
        key: "short_description",
        header: t("pages.knowledgeBases.columns.shortDescription"),
        className: "max-w-xs truncate text-ink-500",
        render: (kb) => kb.short_description ?? "—",
      },
    ],
    [t, teamMap],
  );

  if (isLoading) return <LoadingState />;
  if (isError) return <ErrorState error={error} onRetry={() => void refetch()} />;

  return (
    <div className="flex flex-col gap-6">
      <PageHeader
        title={t("pages.knowledgeBases.title")}
        description={t("pages.knowledgeBases.description")}
        actions={
          <>
            <Button
              variant={showInactive ? "secondary" : "outline"}
              onClick={() => setShowInactive((v) => !v)}
            >
              {showInactive
                ? t("pages.knowledgeBases.hideInactive")
                : t("pages.knowledgeBases.showInactive")}
            </Button>
            <Button onClick={() => setFormOpen(true)} disabled={!canCreate}>
              <Plus className="h-4 w-4" />
              {t("pages.knowledgeBases.new")}
            </Button>
          </>
        }
      />

      <div className="grid gap-3 sm:grid-cols-2 lg:grid-cols-4">
        <StatCard
          label={t("pages.knowledgeBases.summary.totalKbs")}
          value={summary.data?.total_knowledge_bases.toLocaleString("pt-BR") ?? "—"}
          icon={Database}
          loading={summary.isLoading}
        />
        <StatCard
          label={t("pages.knowledgeBases.summary.totalDocuments")}
          value={summary.data?.total_documents.toLocaleString("pt-BR") ?? "—"}
          icon={FileText}
          loading={summary.isLoading}
        />
        <StatCard
          label={t("pages.knowledgeBases.summary.totalSize")}
          value={
            summary.data
              ? `${summary.data.total_size_gb.toLocaleString("pt-BR", {
                  minimumFractionDigits: 2,
                  maximumFractionDigits: 2,
                })} GB`
              : "—"
          }
          icon={HardDrive}
          loading={summary.isLoading}
        />
        <StatCard
          label={t("pages.knowledgeBases.summary.connectedAgents")}
          value={summary.data?.connected_agents.toLocaleString("pt-BR") ?? "—"}
          icon={Bot}
          loading={summary.isLoading}
        />
      </div>

      <div className="flex flex-wrap items-center gap-2">
        <SearchInput
          value={search}
          onChange={setSearch}
          placeholder={t("pages.knowledgeBases.searchPlaceholder")}
          className="w-full sm:w-64"
        />
        <select
          value={teamFilter}
          onChange={(e) => setTeamFilter(e.target.value)}
          className="h-10 rounded-lg border border-line bg-surface px-3 text-sm text-ink-950"
          aria-label={t("pages.knowledgeBases.columns.team")}
        >
          <option value="">{t("pages.knowledgeBases.allTeams")}</option>
          {teams?.map((tm) => (
            <option key={tm.id} value={tm.id}>
              {tm.name}
            </option>
          ))}
        </select>
        <ViewToggle
          value={view}
          onChange={setView}
          labels={{
            cards: t("pages.knowledgeBases.viewCards"),
            list: t("pages.knowledgeBases.viewList"),
          }}
          className="ml-auto"
        />
      </div>

      {data && data.length === 0 ? (
        <EmptyState
          title={t("pages.knowledgeBases.empty")}
          description={t("pages.knowledgeBases.emptyHint")}
        />
      ) : filtered.length === 0 ? (
        <EmptyState title={t("pages.knowledgeBases.noResults")} />
      ) : view === "list" ? (
        <DataTable
          columns={columns}
          rows={filtered}
          getRowKey={(kb) => kb.id}
          getRowHref={(kb) => `/knowledge-bases/${kb.id}`}
        />
      ) : (
        <ul className="grid gap-3 sm:grid-cols-2 lg:grid-cols-3">
          {filtered.map((kb) => {
            const team = teamMap.get(kb.owner_team_id);
            return (
              <li key={kb.id} className="h-full">
                <KbCard
                  kb={kb}
                  ownerTeamName={team?.name}
                  ownerTeamColor={team?.color}
                />
              </li>
            );
          })}
        </ul>
      )}

      <KbFormDialog open={formOpen} onOpenChange={setFormOpen} kb={null} />
    </div>
  );
}
